/**
 * Live node positions.
 *
 * Module-level and deliberately NOT in zustand and NOT persisted: a few
 * thousand coordinates mutating every simulation tick would write megabytes to
 * localStorage. What must survive a rebuild is layout, and that is all this is.
 */
import type { GraphAdjacency, GraphNode } from "./types";

export interface NodePos {
  x: number;
  y: number;
  vx: number;
  vy: number;
}

type Scope = "global" | "local";

const registries: Record<Scope, Map<string, NodePos>> = {
  global: new Map(),
  local: new Map(),
};

export function positionsFor(scope: Scope): Map<string, NodePos> {
  return registries[scope];
}

export function resetPositions(scope: Scope): void {
  registries[scope].clear();
}

/**
 * Reconcile the registry against a new node set.
 *
 * Kept nodes keep their exact position and velocity. Genuinely new nodes are
 * seeded at the centroid of their already-placed neighbours, which is what
 * makes growth look like accretion rather than teleportation; nodes with no
 * placed neighbour go on a golden-angle ring sized to the existing cloud.
 */
export function carryOver(
  scope: Scope,
  nodes: GraphNode[],
  adjacency: Map<string, GraphAdjacency>,
  linkDistance: number,
): void {
  const reg = registries[scope];
  const wanted = new Set(nodes.map((n) => n.id));
  for (const id of [...reg.keys()]) if (!wanted.has(id)) reg.delete(id);

  const placed = new Set(reg.keys());
  const ringR = Math.sqrt(Math.max(1, placed.size)) * 12 + linkDistance * 0.5;
  const GOLDEN = 2.399963229728653;
  let fresh = 0;

  for (const n of nodes) {
    if (reg.has(n.id)) continue;
    const a = adjacency.get(n.id);
    const neighbours: NodePos[] = [];
    if (a) {
      for (const id of a.out) {
        const p = reg.get(id);
        if (p && placed.has(id)) neighbours.push(p);
      }
      for (const id of a.in) {
        const p = reg.get(id);
        if (p && placed.has(id)) neighbours.push(p);
      }
    }
    if (neighbours.length) {
      let cx = 0;
      let cy = 0;
      for (const p of neighbours) {
        cx += p.x;
        cy += p.y;
      }
      cx /= neighbours.length;
      cy /= neighbours.length;
      const j = linkDistance * 0.35;
      reg.set(n.id, {
        x: cx + (Math.random() - 0.5) * j,
        y: cy + (Math.random() - 0.5) * j,
        vx: 0,
        vy: 0,
      });
    } else {
      const angle = fresh * GOLDEN;
      reg.set(n.id, { x: Math.cos(angle) * ringR, y: Math.sin(angle) * ringR, vx: 0, vy: 0 });
    }
    fresh++;
  }
}

/** Snapshot positions back out of the sim so a rebuild can carry them over. */
export function writeBack(scope: Scope, nodes: GraphNode[], pos: Float32Array): void {
  const reg = registries[scope];
  for (let i = 0; i < nodes.length; i++) {
    const n = nodes[i];
    if (!n) continue;
    const p = reg.get(n.id);
    const x = pos[i * 2] ?? 0;
    const y = pos[i * 2 + 1] ?? 0;
    if (p) {
      p.x = x;
      p.y = y;
    } else {
      reg.set(n.id, { x, y, vx: 0, vy: 0 });
    }
  }
}
